import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { SpacerVertical, SpacerHorizontal, Separator } from '@klarna/bubble-ui';
import { Error, HorizontalWrapper } from 'moet-core';
import { Row, Column, GridContainer, withResponsive } from 'mage-components';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';
import { translator } from 'mage-i18n';
import { CAPS, APP_PATHS } from '@/constants';
import { clearErrors } from '@/actions/products';
import { withCapabilityBeforeNext } from '@/flow/common';
import {
  getDataForStoreDetailsSummary,
  getDataForAdditionalProducts,
  getSelectedProductTermAndConditionsLink,
  getProductOptionsWithPricesAndNames,
} from '@/selectors/merchant';
import {
  getProductsError,
  getSelectedProductOptionIds,
  getSelectedProductId,
} from '@/selectors/products';
import { isActivatingProduct } from '@/selectors/loadingStates';
import { logEvent } from '@/utils/analytics';

import { TranslatedSidebarBlock } from '@/components/shared-deprecated';
import { BackButton, CallToActionButton } from '@/components';
import ProductPricesBox from './ProductPricesBox';
import Indicator from './Indicator';
import SummaryStoreDetails from '@/pages/ApplicationOverview/SummaryStoreDetails';
import { CY_ADDITIONAL_PRODUCT_PAGE_IDS } from './constants';
import { ANALYTICS_EVENTS } from './analyticsEvents';

const styleBorder = {
  border: '1px solid',
  borderColor: '#E9E9E8',
  borderRadius: 10,
};

const tKey = 'products-fe.pages.additional_products_page';

class AdditionalProductConfirmation extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isTermsAccepted: false,
    };

    this.onChangeTermsAcceptance = this.onChangeTermsAcceptance.bind(this);
    this.onNext = this.onNext.bind(this);
  }

  onChangeTermsAcceptance() {
    this.setState({
      isTermsAccepted: !this.state.isTermsAccepted,
    });
  }

  onNext() {
    this.props.onNext({
      scrollBeforeNext: true,
      payload: this.props.dataForAddingProducts,
    });
  }

  render() {
    const {
      basePath,
      history,
      isActivatingProduct,
      onClearErrors,
      products,
      selectedProductId,
      storeDetailsForSummary,
      t,
      error = false,
      selectedProductOptionIds = [],
      termsAndConditionsLink = '',
    } = this.props;

    return (
      <GridContainer padding={{ mobile: 4 }}>
        <Row>
          <Column>
            <Indicator products={products} />
            <SpacerVertical spaceToken="space/300" />
          </Column>
        </Row>
        <Row>
          <Column columns={{ tablet: 3 }}>
            <TranslatedSidebarBlock tKeyPrefix={`${tKey}.sidebar_left.we_have_your_data`} />
          </Column>
          <Column columns={{ tablet: 9 }}>
            {error && <Error message={t('products-fe.errors.general')} onClick={onClearErrors} />}
            <Separator />
            <SpacerVertical spaceToken="space/300" />
            <SummaryStoreDetails storeDetails={storeDetailsForSummary} />
            <SpacerVertical spaceToken="space/300" />
            <Separator />
          </Column>
        </Row>
        <SpacerVertical spaceToken="space/800" />
        <Row additionalStyles={styleBorder}>
          <Column>
            <ProductPricesBox
              products={products}
              selectedProductOptionIds={selectedProductOptionIds}
              selectedProductId={selectedProductId}
              isTocAccepted={this.state.isTermsAccepted}
              onChange={this.onChangeTermsAcceptance}
              referrer={APP_PATHS.ADDITIONAL_PRODUCT_CONFIRMATION_PATH}
              history={history}
              termsAndConditionsLink={termsAndConditionsLink}
            />
          </Column>
        </Row>
        <SpacerVertical spaceToken="space/800" />
        <Row>
          <Column numberOfColumns={8}>
            <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
              <BackButton
                onClick={() => {
                  history.push(
                    `${basePath}/${APP_PATHS.PRODUCT_SELECTION_PATH}` + history.location.search,
                  );
                }}
              >
                {t(`${tKey}.main.buttons.back`)}
              </BackButton>
              <SpacerHorizontal spaceToken="space/300" />
              <CallToActionButton
                loading={isActivatingProduct}
                disabled={isActivatingProduct || !this.state.isTermsAccepted}
                onClick={this.onNext}
                {...generateCyElemId(CY_ADDITIONAL_PRODUCT_PAGE_IDS.BTN_SUBMIT, window)}
              >
                {t(`${tKey}.main.buttons.submit`)}
              </CallToActionButton>
            </HorizontalWrapper>
          </Column>
        </Row>
      </GridContainer>
    );
  }
}

AdditionalProductConfirmation.propTypes = {
  basePath: PropTypes.string.isRequired,
  dataForAddingProducts: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  isActivatingProduct: PropTypes.bool.isRequired,
  products: PropTypes.array.isRequired,
  selectedProductId: PropTypes.string.isRequired,
  storeDetailsForSummary: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  onClearErrors: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  error: PropTypes.bool,
  selectedProductOptionIds: PropTypes.array,
  termsAndConditionsLink: PropTypes.string,
};

export default compose(
  connect(
    state => ({
      dataForAddingProducts: getDataForAdditionalProducts(state),
      error: getProductsError(state),
      isActivatingProduct: isActivatingProduct(state),
      products: getProductOptionsWithPricesAndNames(state),
      selectedProductId: getSelectedProductId(state),
      selectedProductOptionIds: getSelectedProductOptionIds(state),
      storeDetailsForSummary: getDataForStoreDetailsSummary(state),
      termsAndConditionsLink: getSelectedProductTermAndConditionsLink(state),
      t: translator(state),
    }),
    {
      onClearErrors: clearErrors,
    },
  ),
  withCapabilityBeforeNext({ cap: CAPS.V2_MERCHANTS_ADDITIONAL_PRODUCTS_POST }, ({ payload }) => {
    const productId = payload.body
      .map(({ id, options = [] }) => (id === 'kco' ? id : options.map(({ id }) => id).join(',')))
      .join(',');
    logEvent(ANALYTICS_EVENTS.selectAdditionalProduct, { productId });
  }),
)(withResponsive(AdditionalProductConfirmation));
